// SevenDoors.tsx
// The corridor. Seven doors, one school. Desktop: GSAP-pinned horizontal
// scroll mapped to vertical. Mobile: native CSS scroll-snap for an app-like
// swipe. No prices here. Each door leads to its own landing page.
import { useEffect, useRef, useState, type CSSProperties, type ComponentType } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Home, Crown, Building2, Church, Heart, GraduationCap, Globe, ArrowRight,
} from 'lucide-react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

// Tokens (same family as HomePage)
const M = '#06060c'
const D2 = '#0a0a14'
const CARD = '#0d0d15'
const BLUE = '#0ea5e9'
const G = '#d4a843'
const TX = '#ffffff'
const MU = 'rgba(255,255,255,0.55)'
const MU2 = 'rgba(255,255,255,0.35)'
const serif = "'Playfair Display', Georgia, serif"
const sans = "'Inter', system-ui, sans-serif"

type Door = {
  id: string
  icon: ComponentType<{ size?: number; strokeWidth?: number }>
  eyebrow: string
  title: string
  line: string
  cta: string
  to: string
  accent: string
}

const DOORS: Door[] = [
  {
    id: 'families',
    icon: Home,
    accent: '#d4a843',
    eyebrow: 'For the home',
    title: 'Families & Homeschool',
    line: 'The complete school for your children. A personal AI tutor, real records, and a rhythm that fits your family.',
    cta: 'Explore the Academy',
    to: '/homeschool',
  },
  {
    id: 'elite',
    icon: Crown,
    accent: '#f053e6ff',
    eyebrow: 'For the few',
    title: 'Elite Academy',
    line: 'The finishing-school curriculum. Etiquette, rhetoric, wealth, diplomacy. What the great schools teach privately.',
    cta: 'See the Curriculum',
    to: '/elite',
  },
  {
    id: 'whitelabel',
    icon: Building2,
    accent: '#0ea5e9',
    eyebrow: 'For any organisation',
    title: 'Your Own Platform',
    line: 'White-label the entire engine under your brand: Universities, Schools, Businesses, Charities & NGOs.',
    cta: 'Learn More',
    to: '/platform',
  },
  {
    id: 'church',
    icon: Church,
    accent: '#f59e0b',
    eyebrow: 'For the congregation',
    title: 'Churches',
    line: 'A Bible College for your whole congregation, taught verse by verse with full oversight.',
    cta: 'Bring Your Church',
    to: '/church',
  },
  {
    id: 'sponsor',
    icon: Heart,
    accent: '#fb7185',
    eyebrow: 'For a child',
    title: 'Sponsor a Child',
    line: 'Fund a complete education for a child who cannot pay, and watch their progress on your own dashboard.',
    cta: 'Meet Ava First',
    to: '/sponsor',
  },
  {
    id: 'university',
    icon: GraduationCap,
    accent: '#10b981',
    eyebrow: 'For the scholar',
    title: 'University',
    line: 'Forty-five degree programs taught conversationally, from technology to theology.',
    cta: 'Browse the Degrees',
    to: '/university',
  },
  {
    id: 'ngo',
    icon: Globe,
    accent: '#14b8a6',
    eyebrow: 'For the community',
    title: 'Charities & NGOs',
    line: 'Bring education to the families you serve, through your own partner portal.',
    cta: 'Become a Partner',
    to: '/ngo',
  },
  {
    id: 'schools',
    icon: Building2,
    accent: '#1828a4ff',
    eyebrow: 'For the classroom',
    title: 'Schools & Teachers',
    line: 'Your classroom, your curriculum, our engine. Assignments, oversight, notifications, and printable records.',
    cta: 'Talk to Us',
    to: '/schools',
  },
  
]

// Injected once. Class-based hover states are more performant than JS handlers.
const STYLES = `
  .swa-door {
    transition: transform 500ms cubic-bezier(0.2,0.8,0.2,1),
                border-color 500ms ease,
                box-shadow 500ms ease;
  }
  .swa-door:focus-visible {
    outline: 2px solid ${G};
    outline-offset: 6px;
  }
  .swa-door-cta svg {
    transition: transform 320ms cubic-bezier(0.2,0.8,0.2,1);
  }
  @media (hover: hover) and (min-width: 768px) {
    .swa-door:hover {
      transform: translateY(-8px);
      border-color: color-mix(in srgb, var(--accent) 45%, transparent);
      box-shadow: 0 40px 90px -30px color-mix(in srgb, var(--accent) 40%, transparent),
                  0 0 0 1px color-mix(in srgb, var(--accent) 18%, transparent) inset;
    }
    .swa-door:hover .swa-door-glow { opacity: 1; }
    .swa-door:hover .swa-door-cta svg { transform: translateX(5px); }
  }
  .swa-track-mobile { scrollbar-width: none; -ms-overflow-style: none; }
  .swa-track-mobile::-webkit-scrollbar { display: none; }
`

function DoorCard({
  door, index, total, isMobile,
}: { door: Door; index: number; total: number; isMobile: boolean }) {
  const { t } = useTranslation()
  const Icon = door.icon
  const num = String(index + 1).padStart(2, '0')
  const totalStr = String(total).padStart(2, '0')

  const cardStyle: CSSProperties & { '--accent'?: string } = {
    '--accent': door.accent,
    position: 'relative',
    flex: '0 0 auto',
    width: isMobile ? '84vw' : 'min(460px, 40vw)',
    height: isMobile ? undefined : 'clamp(360px, 54vh, 620px)',
    minHeight: isMobile ? '66vh' : undefined,
    scrollSnapAlign: isMobile ? 'center' : undefined,
    textDecoration: 'none',
    color: TX,
    borderRadius: 28,
    padding: '36px 32px 32px',
    display: 'flex',
    flexDirection: 'column',
    background: `linear-gradient(160deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.005) 100%), ${CARD}`,
    border: '1px solid rgba(255,255,255,0.07)',
    backdropFilter: 'blur(20px) saturate(140%)',
    WebkitBackdropFilter: 'blur(20px) saturate(140%)',
    overflow: 'hidden',
    isolation: 'isolate',
  }

  return (
    <Link
      to={door.to}
      className="swa-door"
      style={cardStyle}
      aria-label={`${door.title}. ${door.cta}`}
    >
      {/* ambient accent glow (intensifies on hover) */}
      <div
        aria-hidden
        className="swa-door-glow"
        style={{
          position: 'absolute',
          top: -100,
          right: -100,
          width: 340,
          height: 340,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${door.accent}44 0%, transparent 70%)`,
          filter: 'blur(30px)',
          opacity: 0.65,
          transition: 'opacity 600ms ease',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* top row: icon + ordinal */}
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div
          style={{
            width: 54,
            height: 54,
            borderRadius: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `${door.accent}1a`,
            border: `1px solid ${door.accent}55`,
            color: door.accent,
            boxShadow: `0 0 50px ${door.accent}33, inset 0 0 20px ${door.accent}11`,
          }}
        >
          <Icon size={22} strokeWidth={1.75} />
        </div>
        <div style={{ textAlign: 'right', lineHeight: 1 }}>
          <div style={{ fontFamily: serif, fontSize: 24, fontWeight: 700, color: TX }}>
            {num}
          </div>
          <div style={{ fontSize: 10, letterSpacing: '0.25em', color: MU2, fontWeight: 700, marginTop: 4 }}>
            / {totalStr}
          </div>
        </div>
      </div>

      {/* eyebrow */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          marginTop: 30,
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          color: door.accent,
        }}
      >
        {t(`home.doors.${door.id}Eyebrow`, door.eyebrow)}
      </div>

      {/* title */}
      <h3
        style={{
          position: 'relative',
          zIndex: 1,
          fontFamily: serif,
          fontSize: isMobile ? '1.55rem' : 'clamp(1.5rem, 1.6vw, 1.85rem)',
          fontWeight: 700,
          lineHeight: 1.1,
          letterSpacing: '-0.015em',
          margin: '12px 0 0',
          color: TX,
        }}
      >
        {t(`home.doors.${door.id}`, door.title)}
      </h3>

      {/* accent rule */}
      <div
        style={{
          width: 36,
          height: 2,
          background: door.accent,
          margin: '20px 0',
          borderRadius: 2,
          boxShadow: `0 0 12px ${door.accent}88`,
        }}
      />

      {/* description */}
      <p
        style={{
          position: 'relative',
          zIndex: 1,
          color: MU,
          fontSize: 14,
          lineHeight: 1.7,
          margin: 0,
          flex: 1,
        }}
      >
        {t(`home.doors.${door.id}Line`, door.line)}
      </p>

      {/* CTA */}
      <div
        className="swa-door-cta"
        style={{
          position: 'relative',
          zIndex: 1,
          marginTop: 28,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          color: door.accent,
          fontWeight: 800,
          fontSize: 13,
          letterSpacing: '0.02em',
        }}
      >
        {t(`home.doors.${door.id}Cta`, door.cta)}
        <ArrowRight size={15} strokeWidth={2.5} />
      </div>
    </Link>
  )
}

export default function SevenDoors() {
  const { t } = useTranslation()
  const sectionRef = useRef<HTMLElement>(null)
  const pinnedRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const progressRef = useRef<HTMLDivElement>(null)
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  )

  // Style tag injection (safe for React DOM tree)
  useEffect(() => {
    const styleId = 'swa-seven-doors-styles'
    if (!document.getElementById(styleId)) {
      const el = document.createElement('style')
      el.id = styleId
      el.textContent = STYLES
      document.head.appendChild(el)
    }
  }, [])

  // Breakpoint
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)')
    const update = () => setIsMobile(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])

  // GSAP horizontal corridor (desktop only)
  useEffect(() => {
    if (isMobile) return
    const section = sectionRef.current
    const pinned = pinnedRef.current
    const track = trackRef.current
    if (!section || !pinned || !track) return

    const ctx = gsap.context(() => {
      const getAmount = () =>
        Math.max(0, track.getBoundingClientRect().width - window.innerWidth)

      const tween = gsap.to(track, {
        x: () => -getAmount(),
        ease: 'none',
      })

      ScrollTrigger.create({
        trigger: section,
        pin: pinned,
        start: 'top top',
        end: () => `+=${getAmount()}`,
        scrub: 1,
        animation: tween,
        invalidateOnRefresh: true,
        anticipatePin: 1,
        onUpdate: (self) => {
          if (progressRef.current) {
            progressRef.current.style.transform = `scaleX(${self.progress})`
          }
        },
      })

      // Refresh after fonts settle so measurements are exact
      if (document.fonts?.ready) {
        document.fonts.ready.then(() => ScrollTrigger.refresh())
      }
    }, section)

    return () => ctx.revert()
  }, [isMobile])

  return (
    <section
      ref={sectionRef}
      id="paths"
      style={{
        position: 'relative',
        background: D2,
      }}
    >
      <div
        ref={pinnedRef}
        style={{
          position: 'relative',
          overflow: 'hidden',
          ...(isMobile
            ? {}
            : { height: '100vh', display: 'flex', flexDirection: 'column' }),
        }}
      >

      {/* architectural columns: faint vertical rhythm behind the doors */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          backgroundImage:
            'repeating-linear-gradient(90deg, rgba(255,255,255,0.022) 0px, rgba(255,255,255,0.022) 1px, transparent 1px, transparent 140px)',
          maskImage:
            'linear-gradient(180deg, transparent 0%, #000 18%, #000 82%, transparent 100%)',
          WebkitMaskImage:
            'linear-gradient(180deg, transparent 0%, #000 18%, #000 82%, transparent 100%)',
        }}
      />

      {/* Header (stays visible while pinned on desktop) */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          flexShrink: 0,
          width: '100%',
          maxWidth: 1440,
          margin: '0 auto',
          padding: isMobile ? '72px 24px 28px' : '68px 64px 20px',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 24,
        }}
      >
        <div style={{ maxWidth: 640 }}>
          <p
            style={{
              fontSize: 11,
              color: BLUE,
              fontWeight: 800,
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              margin: '0 0 14px',
            }}
          >
            {t('home.junction.eyebrow', 'One school, eight doors')}
          </p>
          <h2
            style={{
              fontFamily: serif,
              fontSize: 'clamp(2rem, 4.2vw, 3.2rem)',
              fontWeight: 700,
              lineHeight: 1.05,
              letterSpacing: '-0.02em',
              margin: 0,
              color: TX,
            }}
          >
            {t('home.junction.title1', 'Where would you like')}{' '}
            <span style={{ color: G }}>{t('home.junction.title2', 'to begin?')}</span>
          </h2>
        </div>
        {!isMobile && (
          <div
            style={{
              color: MU2,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              paddingBottom: 6,
            }}
          >
            {t('home.junction.hint', 'Scroll to walk the hall')}
          </div>
        )}
      </div>

      {/* Track viewport */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          flex: isMobile ? undefined : 1,
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          overflow: isMobile ? 'visible' : 'hidden',
        }}
      >
        <div
          ref={trackRef}
          className={isMobile ? 'swa-track-mobile' : undefined}
          style={{
            display: 'flex',
            gap: isMobile ? 16 : 28,
            padding: isMobile ? '20px 24px 56px' : '24px 64px',
            width: isMobile ? undefined : 'max-content',
            willChange: isMobile ? undefined : 'transform',
            ...(isMobile
              ? {
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  scrollSnapType: 'x mandatory',
                  WebkitOverflowScrolling: 'touch',
                  overscrollBehaviorX: 'contain',
                  maxWidth: '100%',
                }
              : {}),
          }}
        >
          {DOORS.map((door, i) => (
            <DoorCard
              key={door.id}
              door={door}
              index={i}
              total={DOORS.length}
              isMobile={isMobile}
            />
          ))}
        </div>
      </div>

      {/* Progress rail (desktop only) */}
      {!isMobile && (
        <div
          style={{
            flexShrink: 0,
            width: '100%',
            maxWidth: 1440,
            margin: '0 auto',
            padding: '0 64px 40px',
          }}
        >
          <div
            style={{
              height: 1,
              background: 'rgba(255,255,255,0.08)',
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 1,
            }}
          >
            <div
              ref={progressRef}
              style={{
                position: 'absolute',
                inset: 0,
                background: `linear-gradient(90deg, ${G}, ${BLUE})`,
                transformOrigin: 'left center',
                transform: 'scaleX(0)',
                willChange: 'transform',
              }}
            />
          </div>
        </div>
      )}
      </div>
    </section>
  )
}